import { useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageCircle, Phone, CheckCircle, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { trackEvent } from "@/utils/tracking";
import { trackEvent as internalTrack } from "@/utils/trackingClient";
import { openWhatsApp as openWhatsAppHelper } from "@/utils/phone";
import { trackLead, toE164, setEnhancedConversionData } from "@/lib/tracking";
import { getServiceLandingBySlug } from "@/data/serviceLandingData";

const ThankYou = ({ settings }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tracked = useRef(false);

  const leadData = location.state || {};
  const gewerk = new URLSearchParams(location.search).get("gewerk") || null;

  // lead_pending (gesetzt beim erfolgreichen Absenden) einmalig lesen — mit 30-min-TTL
  const pendingRef = useRef(undefined);
  if (pendingRef.current === undefined) {
    try {
      const p = JSON.parse(sessionStorage.getItem("lead_pending") || "null");
      pendingRef.current = p && Date.now() - (p.ts || 0) < 30 * 60 * 1000 ? p : null;
      if (p && !pendingRef.current) sessionStorage.removeItem("lead_pending");
    } catch {
      pendingRef.current = null;
    }
  }
  const pending = pendingRef.current;

  const hasLead = !!leadData.lead_id || !!pending;
  const whatsappNumber = settings?.whatsapp_number || "4917620283482";

  // noindex: Danke-Seite nicht indexieren
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  // Conversion NUR bei echtem Absenden (lead_pending-Sperre) — Reload/Direktaufruf zählt nicht
  useEffect(() => {
    if (!tracked.current) {
      tracked.current = true;
      internalTrack('lead_thank_you_view', {
        event_type: 'view',
        extra: { lead_id: leadData.lead_id || pending?.transaction_id || null, service: leadData.service || pending?.service || null },
      });

      if (pending) {
        // Enhanced Conversions: nur bei Marketing-Consent (Prüfung in setEnhancedConversionData)
        setEnhancedConversionData({
          phone: toE164(pending.phone),
          email: pending.email || null,
        });
        // Google-Ads-Formular-Conversion mit transaction_id (einmalig)
        trackLead("formular", gewerk || pending.gewerk, { transactionId: pending.transaction_id });
        try { sessionStorage.removeItem("lead_pending"); } catch { /* noop */ }
      }

      if (pending) {
        // lead_success + Meta Pixel nur beim ECHTEN ersten Besuch (pending vorhanden),
        // nicht bei Reload (history.state enthält lead_id weiterhin)
        trackEvent("lead_success", {
          lead_id: leadData.lead_id || pending?.transaction_id,
          service: leadData.service || pending?.service,
          source: leadData.source,
          has_email: !!(leadData.email || pending?.email),
          has_photos: leadData.has_photos || false,
        });

        // Meta Pixel Lead event
        if (typeof window.fbq === "function") {
          window.fbq("track", "Lead", {
            content_name: leadData.service || pending?.service,
            content_category: leadData.source,
          });
        }
      }
    }
  }, [hasLead, leadData, pending, gewerk]);

  // Redirect if accessed directly (no lead data → no tracking)
  useEffect(() => {
    if (!hasLead) {
      const timer = setTimeout(() => navigate("/"), 5000);
      return () => clearTimeout(timer);
    }
  }, [hasLead, navigate]);

  const openWhatsApp = () => {
    const gewerkService = gewerk ? getServiceLandingBySlug(gewerk)?.formService : null;
    const msg = gewerkService
      ? `Hallo, ich interessiere mich für ${gewerkService}.`
      : `Hallo BEKAJ BAU,\nich habe gerade eine Anfrage über Ihre Webseite geschickt (${leadData.service || pending?.service || "Bauprojekt"}).\nIch würde mich über eine schnelle Rückmeldung freuen.`;
    openWhatsAppHelper(whatsappNumber, msg);
    trackLead("whatsapp", gewerk);
    trackEvent("contact_whatsapp", { source: "thank_you_page" });
  };

  const callNow = () => {
    trackLead("anruf", gewerk);
    trackEvent("call_click", { source: "thank_you_page" });
    window.open(`tel:${settings?.phone || whatsappNumber}`, "_self");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf8f5] to-white flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        <div className="max-w-lg w-full text-center" data-testid="thank-you-page">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          <h1
            className="text-3xl sm:text-4xl font-bold text-[#4A331D] mb-4"
            style={{ fontFamily: "Work Sans, sans-serif" }}
            data-testid="thank-you-headline"
          >
            Vielen Dank für Ihre Anfrage.
          </h1>

          <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
            Wir melden uns schnellstmöglich bei Ihnen zurück.
          </p>

          <div className="bg-white rounded-xl border border-[#e8dfd4] p-6 shadow-sm mb-8">
            <p className="text-sm text-gray-500 mb-4">
              In dringenden Fällen erreichen Sie uns auch direkt per WhatsApp oder telefonisch.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={openWhatsApp}
                className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white py-5 text-base font-semibold rounded-lg shadow-md"
                data-testid="thankyou-whatsapp"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp schreiben
              </Button>
              <Button
                onClick={callNow}
                variant="outline"
                className="flex-1 border-[#4A331D] text-[#4A331D] hover:bg-[#4A331D] hover:text-white py-5 text-base font-semibold rounded-lg"
                data-testid="thankyou-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                Jetzt anrufen
              </Button>
            </div>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#4A331D] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>

          {!hasLead && (
            <p className="text-xs text-gray-400 mt-6">
              Sie werden in Kürze zur Startseite weitergeleitet...
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;
